const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const SELECTORS = [
  '#MainContent > .shopify-section',
  '[data-testid="product-information"] .product-information__media',
  '[data-testid="product-information"] .product-details',
  '.product-card',
  '.collection-card',
  'footer .shopify-section'
];

const processedTargets = new WeakSet();
let observer;

function setupObserver() {
  if (observer || reduceMotion.matches) return;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('aura-reveal--visible');
        observer.unobserve(entry.target);
      });
    },
    {
      root: null,
      rootMargin: '0px 0px -8% 0px',
      threshold: 0,
    }
  );
}

function initAuraMotion(root = document) {
  if (reduceMotion.matches) return;
  setupObserver();

  const candidates = new Set();
  for (const selector of SELECTORS) {
    if (root instanceof Element && root.matches(selector)) candidates.add(root);
    root.querySelectorAll(selector).forEach((element) => candidates.add(element));
  }

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const targets = [];
  candidates.forEach((element) => {
    if (!(element instanceof HTMLElement) || processedTargets.has(element)) return;
    if (element.closest('[hidden], [aria-hidden="true"]')) return;

    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    processedTargets.add(element);
    if (rect.top < viewportHeight && rect.bottom > 0) return;
    targets.push(element);
  });

  targets.forEach((element, index) => {
    element.style.setProperty('--aura-reveal-delay', `${Math.min(index % 5, 4) * 55}ms`);
    element.classList.add('aura-reveal');
    observer.observe(element);
  });
}

const pendingRoots = new Set();
let refreshScheduled = false;

const mutationObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof Element) pendingRoots.add(node);
    }
  }

  if (!pendingRoots.size || refreshScheduled) return;
  refreshScheduled = true;
  requestAnimationFrame(() => {
    refreshScheduled = false;
    pendingRoots.forEach((root) => {
      if (root.isConnected) initAuraMotion(root);
    });
    pendingRoots.clear();
  });
});

function startAuraMotion() {
  initAuraMotion();
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startAuraMotion, { once: true });
} else {
  startAuraMotion();
}

document.addEventListener('shopify:section:load', (event) => {
  initAuraMotion(event.target instanceof Element ? event.target : document);
});

reduceMotion.addEventListener?.('change', () => {
  if (reduceMotion.matches) {
    observer?.disconnect();
    observer = undefined;
    document.querySelectorAll('.aura-reveal').forEach((element) => {
      element.classList.add('aura-reveal--visible');
    });
  } else {
    initAuraMotion();
  }
});
